// client/src/components/secure/AgentHome.js — what an agent login sees at /dashboard (and so
// what the BREC app opens on: the app starts at /portal, which goes to /dashboard).
//
// Owner, 24 Oct 2026: agents need "a portal where they can do MLS type searches for search
// capabilities we have developed and the zoning searches capabilities we have developed". So the
// two searches are the first, largest things on the screen, then the agent's own saved searches,
// new matches for their clients and the market snapshot, then their own clients and today's list.
// Every count comes from a route that already narrows to the agent's own book (lib/agentScope).
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import TodayPanel from './TodayPanel';
import TrainingCard from './training/TrainingCard';

const SEARCH_TILES = [
    { to: '/dashboard/property-search', title: 'Property search (MLS-type)', blurb: 'Our listings and the MLS index: homes, commercial, land, for sale or lease. Save a search for a client, share a collection.', tone: 'from-slate-800 to-slate-900' },
    { to: '/dashboard/property-search?tab=zoning', title: 'Zoning search — what can go where', blurb: 'Pick a business and see which towns allow it by right, and every known property zoned for it.', tone: 'from-emerald-700 to-emerald-900' },
];

const Count = ({ n }) => (n == null ? <span className="text-slate-300">—</span> : <span>{n}</span>);

export default function AgentHome({ user }) {
    const api = useApiClient();
    const [counts, setCounts] = useState({ saved: null, matches: null, clients: null });

    useEffect(() => {
        let live = true;
        const len = (p) => api.get(p).then((r) => (Array.isArray(r) ? r.length : null)).catch(() => null);
        Promise.all([
            len('/api/property-search/saved-searches'),
            len('/api/property-search/matches'),
            len('/api/clients'),
        ]).then(([saved, matches, clients]) => { if (live) setCounts({ saved, matches, clients }); });
        return () => { live = false; };
    }, [api]);

    const small = [
        { to: '/dashboard/property-search?tab=saved', title: 'Saved searches', n: counts.saved, line: 'for your clients' },
        { to: '/dashboard/property-search?tab=matches', title: 'New matches for my clients', n: counts.matches, line: 'waiting for you to look at (nothing is sent by itself)' },
        { to: '/dashboard/property-search?tab=market', title: 'Market snapshot', n: null, line: 'prices and days on market for an area' },
        { to: '/dashboard/clients', title: 'My clients', n: counts.clients, line: 'your book' },
    ];

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-slate-800">Welcome{user?.username ? `, ${user.username}` : ''}</h1>
                <p className="text-sm text-slate-500 mt-0.5">Your searches, your clients and what is due today.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {SEARCH_TILES.map((t) => (
                    <Link key={t.to} to={t.to}
                        className={`block rounded-xl bg-gradient-to-br ${t.tone} p-5 sm:p-6 text-white shadow hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500`}>
                        <span className="block text-lg sm:text-xl font-bold">{t.title}</span>
                        <span className="block mt-1 text-sm text-white/80">{t.blurb}</span>
                        <span className="inline-block mt-3 text-sm font-semibold underline">Open</span>
                    </Link>
                ))}
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {small.map((c) => (
                    <Link key={c.to} to={c.to} className="block bg-white rounded-lg border border-slate-200 p-4 shadow-sm hover:border-slate-400">
                        <span className="block text-xs font-medium text-slate-500">{c.title}</span>
                        <span className="block mt-1 text-2xl font-semibold text-slate-900">{c.title === 'Market snapshot' ? '→' : <Count n={c.n} />}</span>
                        <span className="block mt-0.5 text-xs text-slate-500">{c.line}</span>
                    </Link>
                ))}
            </div>

            <TodayPanel />
            <TrainingCard />

            <p className="text-xs text-slate-500">
                On your phone? <Link to="/dashboard/app" className="text-indigo-700 underline">Install the BREC app</Link> and this page opens first.
            </p>
        </div>
    );
}
